/**
 * Client-side business rules (mirrors server pre-import validation where possible).
 */

function cell(row, ...keys) {
  for (const key of keys) {
    const val = row[key];
    if (val !== undefined && val !== null && val !== '' && val !== 'NA') {
      return val;
    }
  }
  return null;
}

function parseNum(value, fallback = 0) {
  if (value === undefined || value === null || value === '' || value === 'NA') {
    return fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function applyBusinessRules(uploadType, rows) {
  const errors = [];
  const warnings = [];
  const info = [];
  let summary = {};

  if (uploadType === 'loans') {
    const seenLoanNos = {};
    let pathA = 0;
    let pathB = 0;

    rows.forEach((row) => {
      const rn = row.__rowNumber || '?';
      const amount = parseNum(cell(row, 'Amount'));
      const period = parseNum(cell(row, 'Period'));
      const principleBal = parseNum(cell(row, 'Principle balance'), 0);
      const interestBal = parseNum(cell(row, 'Interest balance'), 0);
      const fineBal = parseNum(cell(row, 'Fine balance'), 0);
      const onboardingDate = cell(row, 'Onboarding date (DD/MM/YYYY)');
      const loanNo = cell(row, 'LoanNo');

      if (amount !== null && amount <= 0) {
        errors.push({ row: rn, message: 'Amount must be positive (original loan amount).' });
      }
      if (period !== null && period <= 0) {
        errors.push({ row: rn, message: 'Period must be positive.' });
      }
      if (amount !== null && principleBal > amount) {
        errors.push({ row: rn, message: `Principle balance cannot exceed Amount (${amount}).` });
      }
      if (principleBal < 0 || interestBal < 0 || fineBal < 0) {
        errors.push({ row: rn, message: 'Balances cannot be negative.' });
      }

      if (principleBal === 0 && interestBal === 0 && fineBal === 0) {
        pathB += 1;
        warnings.push({
          row: rn,
          message: 'Zero balances — full issuance (Path B). Import repayments separately.',
        });
      } else {
        pathA += 1;
        if (!onboardingDate) {
          errors.push({ row: rn, message: 'Onboarding date required when balances are set.' });
        }
      }

      if (loanNo) {
        const key = String(loanNo).trim();
        if (seenLoanNos[key]) {
          errors.push({ row: rn, message: `Duplicate LoanNo "${key}" (also row ${seenLoanNos[key]}).` });
        } else {
          seenLoanNos[key] = rn;
        }
      }
    });

    summary = { path_a: pathA, path_b: pathB };
    if (pathA && !pathB) {
      info.push({
        row: null,
        message: `${pathA} loan(s): Path A snapshot — skip loan repayments import unless adding new history.`,
      });
    }
    if (pathA && pathB) {
      warnings.push({
        row: null,
        message: 'Mixed Path A and Path B rows in one file — confirm this is intentional.',
      });
    }
  }

  if (uploadType === 'loanschedules') {
    rows.forEach((row) => {
      const rn = row.__rowNumber || '?';
      const loanNo = cell(row, 'LoanNo');
      const repaymentNum = cell(row, 'Repayment number');
      const principle = parseNum(cell(row, 'Principle'), 0);
      const interest = parseNum(cell(row, 'Interest'), 0);

      if (!loanNo) {
        errors.push({ row: rn, message: 'LoanNo is required.' });
      }
      if (!repaymentNum) {
        errors.push({ row: rn, message: 'Repayment number is required.' });
      }
      if (!cell(row, 'Installment date (DD/MM/YYYY)')) {
        errors.push({ row: rn, message: 'Installment date is required.' });
      }
      if (principle < 0 || interest < 0) {
        errors.push({ row: rn, message: 'Principle and Interest cannot be negative.' });
      }
    });
    info.push({
      row: null,
      message: 'Schedules replace auto-generated installments for each LoanNo in this file.',
    });
  }

  if (uploadType === 'loanrepayments') {
    rows.forEach((row) => {
      const rn = row.__rowNumber || '?';
      const principle = parseNum(cell(row, 'Principle'), 0);
      const interest = parseNum(cell(row, 'Interest'), 0);
      const fine = parseNum(cell(row, 'Fine'), 0);
      const loanId = cell(row, 'LoanId');

      if (!loanId) {
        errors.push({ row: rn, message: 'LoanId required (must match LoanNo, not database ID).' });
      }
      if (principle <= 0 && interest <= 0 && fine <= 0) {
        errors.push({ row: rn, message: 'At least one of Principle, Interest, or Fine must be > 0.' });
      }
    });
    info.push({
      row: null,
      message: 'Path B only — do not use if running loans already had Principle/Interest balances set.',
    });
  }

  if (uploadType === 'coa_opening_balances') {
    const journalTotals = {};

    rows.forEach((row) => {
      const rn = row.__rowNumber || '?';
      const account = cell(row, 'Account');
      const debit = parseNum(cell(row, 'Debit'), 0);
      const credit = parseNum(cell(row, 'Credit'), 0);
      const txnDate = cell(row, 'Date');

      if (!txnDate) {
        errors.push({ row: rn, message: 'Date is required.' });
      }
      if (!account) {
        errors.push({ row: rn, message: 'Account is required.' });
      }
      if (debit === null || credit === null) {
        errors.push({ row: rn, message: 'Debit and Credit must be numbers.' });
      } else {
        if (debit < 0 || credit < 0) {
          errors.push({ row: rn, message: 'Debit and Credit cannot be negative.' });
        }
        if (debit > 0 && credit > 0) {
          errors.push({ row: rn, message: 'Use either Debit or Credit on a row, not both.' });
        }
        if (debit === 0 && credit === 0) {
          errors.push({ row: rn, message: 'At least one of Debit or Credit must be greater than zero.' });
        }
      }

      const journalKey = cell(row, 'JournalNo') || '__auto_opening__';
      if (!journalTotals[journalKey]) {
        journalTotals[journalKey] = { debit: 0, credit: 0 };
      }
      journalTotals[journalKey].debit += Math.max(debit || 0, 0);
      journalTotals[journalKey].credit += Math.max(credit || 0, 0);
    });

    Object.entries(journalTotals).forEach(([journalKey, totals]) => {
      const diff = Math.round((totals.debit - totals.credit) * 100) / 100;
      if (Math.abs(diff) > 0.01) {
        const label = journalKey === '__auto_opening__' ? 'auto-assigned journal' : `JournalNo ${journalKey}`;
        errors.push({
          row: null,
          message: `${label} is out of balance by ${Math.abs(diff).toLocaleString()} (debits ${totals.debit.toLocaleString()}, credits ${totals.credit.toLocaleString()}).`,
        });
      }
    });

    info.push({
      row: null,
      message: 'Each journal group must balance: debits = credits per JournalNo (or one group if JournalNo is blank).',
    });
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    info,
    summary,
  };
}

export function mergeValidationStats(parseStats, businessValidation) {
  return {
    ...parseStats,
    businessErrors: businessValidation.errors,
    businessWarnings: businessValidation.warnings,
    businessInfo: businessValidation.info,
    businessSummary: businessValidation.summary,
    hasBusinessIssues:
      businessValidation.errors.length > 0 || businessValidation.warnings.length > 0,
  };
}
